import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';

/**
 * ProgramDeactivateDialog — S2 V1 single-program contract.
 *
 * Forwards an explicit operator choice to the backend deactivation endpoint.
 * No default selection when there are future visits — the operator must
 * pick "Cancel future visits" or "Keep existing scheduled visits".
 *
 * Props:
 *   isOpen           - boolean
 *   futureVisitCount - number of future non-terminal recurring tasks
 *   submitting       - true while the parent is calling the endpoint
 *   onCancel         - close without changes
 *   onConfirm        - (cancelFutureVisits: boolean) => Promise<void>
 */
const ProgramDeactivateDialog = ({
  isOpen,
  futureVisitCount,
  submitting,
  onCancel,
  onConfirm,
}) => {
  // null = no choice yet (matches the "no default" spec).
  // true  = cancel future visits
  // false = keep existing scheduled visits
  const [choice, setChoice] = useState(null);

  // Reset choice every time the dialog re-opens so a previous selection
  // never leaks into a fresh deactivation flow.
  useEffect(() => {
    if (isOpen) setChoice(null);
  }, [isOpen]);

  const hasVisits = futureVisitCount > 0;
  // When there are no future visits, the spec is a single-button confirm —
  // we treat "no choice needed" as confirm-only and forward false to the
  // endpoint (nothing to cancel).
  const canConfirm = !hasVisits || choice !== null;

  const handleConfirm = () => {
    if (!canConfirm) return;
    const cancelFutureVisits = hasVisits ? choice === true : false;
    onConfirm(cancelFutureVisits);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !submitting && onCancel()}>
      <DialogContent className="max-w-md" data-testid="program-deactivate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Mark Program Inactive?
          </DialogTitle>
          <DialogDescription className="pt-1.5">
            {hasVisits ? (
              <>
                This customer has{' '}
                <strong className="text-gray-900">
                  {futureVisitCount} scheduled future visit{futureVisitCount === 1 ? '' : 's'}
                </strong>
                . What should happen to {futureVisitCount === 1 ? 'it' : 'them'}?
              </>
            ) : (
              <>
                Future recurrence generation will stop. No scheduled visits will
                be affected because none are currently on the calendar.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {hasVisits && (
          <div className="space-y-2 py-1">
            <label
              className={`flex gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                choice === true
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              data-testid="program-deactivate-choice-cancel"
            >
              <input
                type="radio"
                name="program-deactivate-choice"
                checked={choice === true}
                onChange={() => setChoice(true)}
                disabled={submitting}
                className="mt-0.5"
                data-testid="program-deactivate-choice-cancel-radio"
              />
              <div className="text-sm">
                <div className="font-medium text-gray-900">Cancel future visits</div>
                <div className="text-xs text-gray-600 mt-0.5 leading-snug">
                  The {futureVisitCount} scheduled visit{futureVisitCount === 1 ? '' : 's'}{' '}
                  will be cancelled (cancelReason=&apos;program_deactivated&apos;).
                </div>
              </div>
            </label>

            <label
              className={`flex gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                choice === false
                  ? 'border-emerald-300 bg-emerald-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              data-testid="program-deactivate-choice-keep"
            >
              <input
                type="radio"
                name="program-deactivate-choice"
                checked={choice === false}
                onChange={() => setChoice(false)}
                disabled={submitting}
                className="mt-0.5"
                data-testid="program-deactivate-choice-keep-radio"
              />
              <div className="text-sm">
                <div className="font-medium text-gray-900">Keep existing scheduled visits</div>
                <div className="text-xs text-gray-600 mt-0.5 leading-snug">
                  The visits stay on the calendar. Future recurrence generation stops
                  after they complete.
                </div>
              </div>
            </label>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
            data-testid="program-deactivate-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || submitting}
            data-testid="program-deactivate-confirm-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Mark Inactive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProgramDeactivateDialog;
